import { FC, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'

interface ImportDialogProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
  gemeenteSid: number
  gemeenteNaam: string
  categorie: string
  categorieLabel: string
}

const ImportDialog: FC<ImportDialogProps> = ({
  open,
  onClose,
  onComplete,
  gemeenteSid,
  gemeenteNaam,
  categorie,
  categorieLabel,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ imported: number; categorie: string } | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      setError('')
      setResult(null)
    }
  }

  const handleImport = async () => {
    if (!file || !categorie) return

    setLoading(true)
    setError('')
    setResult(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = authToken.getToken() || ''
      const response = await fetch(
        `${getUrl()}/gemeenten/${gemeenteSid}/bevolkingsdata/import?categorie=${encodeURIComponent(categorie)}`,
        {
          method: 'POST',
          headers: { Authorization: token },
          body: formData,
        },
      )

      if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const errorData = await response.json().catch(() => null)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        throw new Error(errorData?.detail || `HTTP ${response.status}`)
      }

      const data = (await response.json()) as { imported: number; categorie: string }
      setResult(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout'
      setError(`Import mislukt: ${message}`)
      console.error('Import failed:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (result) {
      onComplete()
    } else {
      onClose()
    }
    setFile(null)
    setError('')
    setResult(null)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Data importeren — {gemeenteNaam}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Importeert voor categorie <strong>{categorieLabel}</strong>. Het bestand moet de
            kolommen <code>geslacht</code>, <code>leeftijd</code> en één kolom per jaar
            bevatten (zelfde formaat als de export).
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            fullWidth
            sx={{ mb: 2 }}
            disabled={loading}
          >
            {file ? file.name : 'Selecteer bestand (.xlsx)'}
            <input type="file" hidden accept=".xlsx" onChange={handleFileChange} />
          </Button>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {result.imported} records geïmporteerd voor categorie &quot;{categorieLabel}&quot;.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? 'Sluiten' : 'Annuleren'}</Button>
        {!result && (
          <Button
            onClick={() => void handleImport()}
            variant="contained"
            disabled={!file || loading}
          >
            Importeren
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ImportDialog
